import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import InquiryMessageBubble, { InquiryMessage } from '../components/InquiryMessageBubble';
import InquiryCustomerForm, { LinkedCustomer } from '../components/InquiryCustomerForm';

interface AssignedUser {
  id: number;
  full_name: string;
}

interface Attachment {
  id: number;
  original_filename: string;
  mime_type?: string;
  size_bytes?: number;
}

type Message = InquiryMessage;

// Boshqa xodimning xabarini tahrirlash/o'chirish huquqi (backend
// MESSAGE_MODERATOR_ROLES bilan bir xil).
const MESSAGE_MODERATOR_ROLES = ['admin', 'director'];

interface ThreadListItem {
  id: number;
  channel: string;
  customer_name?: string;
  customer_username?: string;
  status: string;
  unread_count: number;
  last_message_snippet?: string;
  last_message_at?: string;
  assigned_user_id?: number;
  assigned_user_name?: string;
  customer_id?: number;
}

interface ThreadDetail {
  id: number;
  channel: string;
  external_chat_id: string;
  business_connection_id?: string;
  customer_name?: string;
  customer_username?: string;
  customer_phone?: string;
  status: string;
  unread_count: number;
  assigned_user_id?: number;
  assignee?: AssignedUser;
  customer_id?: number;
  customer?: LinkedCustomer;
  telegram_can_delete?: boolean;
  last_message_at?: string;
  created_at: string;
  messages: Message[];
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new: { label: 'Yangi', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'Jarayonda', color: 'bg-yellow-100 text-yellow-700' },
  quoted: { label: 'Taklif berildi', color: 'bg-purple-100 text-purple-700' },
  closed: { label: 'Yopildi', color: 'bg-gray-100 text-gray-600' },
};

type AssignedFilter = '' | 'me' | 'unassigned';
const ASSIGNED_FILTERS: { value: AssignedFilter; label: string }[] = [
  { value: '', label: 'Hammasi' },
  { value: 'me', label: 'Menga biriktirilgan' },
  { value: 'unassigned', label: 'Biriktirilmagan' },
];

const errDetail = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const customerDisplayName = (c: LinkedCustomer) => c.contact_person || c.company_name || `Mijoz #${c.id}`;

// Backend bilan bir xil (routers/inquiries.py MAX_OUTBOUND_ATTACHMENT_SIZE,
// core/upload_validation.py INQUIRY_OUTBOUND_EXTENSIONS) - bu yerda faqat
// oldindan ogohlantirish uchun, asosiy tekshiruv serverda.
const MAX_ATTACHMENT_MB = 25;
const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv';

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const POLL_INTERVAL = 30000; // 30 soniya - NotificationBell bilan bir xil naqsh

const timeShort = (s?: string) => {
  if (!s) return '';
  return new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const Inquiries: React.FC = () => {
  const location = useLocation();
  const { canEdit } = usePermissions();
  const { user } = useAuth();
  const canCreateCustomer = canEdit('clients');

  const [threads, setThreads] = useState<ThreadListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState<AssignedFilter>('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replyMode, setReplyMode] = useState<'reply' | 'note'>('reply');
  const [replyFile, setReplyFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [headerError, setHeaderError] = useState('');

  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerJustAdded, setCustomerJustAdded] = useState(false);

  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');
  const [assignableUsers, setAssignableUsers] = useState<AssignedUser[]>([]);
  const [assignableError, setAssignableError] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const openedFromQueryRef = useRef(false);

  const fetchThreads = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (assignedFilter) params.assigned = assignedFilter;
      if (unreadOnly) params.unread = 'true';
      const res = await api.get('/inquiries', { params });
      setThreads(res.data);
      setListError('');
    } catch (e: any) {
      setListError(errDetail(e, "So'rovlar ro'yxatini yuklab bo'lmadi"));
    }
    finally { setLoadingList(false); }
  }, [statusFilter, assignedFilter, unreadOnly]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  // Ro'yxat yangilanishi - mavjud NotificationBell polling naqshiga mos.
  useEffect(() => {
    const interval = setInterval(fetchThreads, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchThreads]);

  useEffect(() => {
    api.get('/inquiries/assignable-users')
      .then(r => { setAssignableUsers(r.data); setAssignableError(''); })
      .catch((e: any) => { setAssignableUsers([]); setAssignableError(errDetail(e, "Xodimlar ro'yxatini yuklab bo'lmadi")); });
  }, []);

  const openThread = useCallback(async (id: number) => {
    setSelectedId(id);
    setMobileView('detail');
    setLoadingDetail(true);
    setDetailError('');
    setReplyError('');
    setHeaderError('');
    setShowCustomerForm(false);
    setCustomerJustAdded(false);
    // Boshqa suhbat uchun tanlangan fayl bu mijozga tasodifan ketmasin.
    setReplyFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      const res = await api.get(`/inquiries/${id}`);
      setDetail(res.data);
      setThreads(prev => prev.map(t => t.id === id ? { ...t, unread_count: 0 } : t));
    } catch (e: any) {
      setDetail(null);
      setDetailError(errDetail(e, "So'rovni ochib bo'lmadi"));
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // NotificationBell ?open=<id> deep-link - CaseList.tsx bilan bir xil naqsh.
  useEffect(() => {
    if (openedFromQueryRef.current) return;
    const openId = new URLSearchParams(location.search).get('open');
    if (openId) {
      openedFromQueryRef.current = true;
      openThread(Number(openId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages.length]);

  const clearReplyFile = () => {
    setReplyFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplyError('');
    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      setReplyError(`Fayl juda katta (${formatFileSize(file.size)}) — ${MAX_ATTACHMENT_MB} MB dan oshmasligi kerak`);
      clearReplyFile();
      return;
    }
    setReplyFile(file);
  };

  const handleSend = async () => {
    const withFile = replyMode === 'reply' && !!replyFile;
    if (!detail || sending || (!replyText.trim() && !withFile)) return;
    setSending(true);
    setReplyError('');
    const text = replyText.trim();
    const isNote = replyMode === 'note';
    try {
      let res: { data: Message };
      if (withFile && replyFile) {
        // Fayl + ixtiyoriy izoh (caption) bitta Telegram xabari bo'lib ketadi.
        const formData = new FormData();
        formData.append('file', replyFile);
        if (text) formData.append('caption', text);
        res = await api.post(`/inquiries/${detail.id}/reply-attachment`, formData);
        clearReplyFile();
      } else {
        // Ichki eslatma alohida endpoint - backend Telegramga umuman yubormaydi.
        res = await api.post(`/inquiries/${detail.id}/${isNote ? 'note' : 'reply'}`, { text });
      }
      setDetail(prev => prev ? { ...prev, messages: [...prev.messages, res.data] } : prev);
      setReplyText('');
      if (!isNote) {
        // Javobdan keyin backend biriktirilmagan thread'ni javob yozganga
        // biriktiradi - header'dagi egani yangilash uchun qayta o'qiymiz.
        refreshDetail(detail.id);
      }
      fetchThreads();
    } catch (e: any) {
      if (withFile && e?.response?.status === 413 && typeof e?.response?.data?.detail !== 'string') {
        // nginx (client_max_body_size) rad etgan - backend'gacha yetmagan.
        setReplyError(`Fayl juda katta — server qabul qilmadi (${MAX_ATTACHMENT_MB} MB gacha)`);
      } else {
        setReplyError(errDetail(e, isNote ? "Eslatmani saqlab bo'lmadi" : withFile ? "Faylni yuborib bo'lmadi" : "Xabarni yuborib bo'lmadi"));
      }
    } finally {
      setSending(false);
    }
  };

  const refreshDetail = async (id: number) => {
    try {
      const res = await api.get(`/inquiries/${id}`);
      setDetail(prev => (prev && prev.id === id ? res.data : prev));
    } catch (e: any) {
      setHeaderError(errDetail(e, "So'rov ma'lumotlarini yangilab bo'lmadi"));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!detail) return;
    setHeaderError('');
    try {
      const res = await api.patch(`/inquiries/${detail.id}`, { status });
      setDetail(res.data);
      fetchThreads();
    } catch (e: any) {
      setHeaderError(errDetail(e, "Holatni o'zgartirib bo'lmadi"));
    }
  };

  const handleAssignChange = async (assignedUserId: string) => {
    if (!detail || !assignedUserId) return;
    setHeaderError('');
    try {
      const res = await api.patch(`/inquiries/${detail.id}`, { assigned_user_id: Number(assignedUserId) });
      setDetail(res.data);
      fetchThreads();
    } catch (e: any) {
      setHeaderError(errDetail(e, "Mas'ulni o'zgartirib bo'lmadi"));
    }
  };

  const canManageMessage = (m: Message) => {
    if (!user || m.is_deleted) return false;
    if (m.direction !== 'outbound' && !m.is_internal) return false; // mijoz xabari - hech qachon
    return m.sender_user_id === user.id || MESSAGE_MODERATOR_ROLES.includes(user.role);
  };

  const handleMessageUpdated = (updated: Message) => {
    setDetail(prev => prev ? { ...prev, messages: prev.messages.map(x => x.id === updated.id ? updated : x) } : prev);
    fetchThreads(); // ro'yxat snippet'i (masalan "O'chirilgan xabar") yangilansin
  };

  const handleCustomerCreated = (customer: LinkedCustomer) => {
    setDetail(prev => prev ? {
      ...prev,
      customer_id: customer.id,
      customer,
      customer_name: customer.contact_person || prev.customer_name,
      customer_phone: customer.phone || prev.customer_phone,
    } : prev);
    setShowCustomerForm(false);
    setCustomerJustAdded(true);
    fetchThreads();
  };

  const downloadAttachment = async (att: Attachment) => {
    if (!detail) return;
    try {
      const res = await api.get(`/inquiries/${detail.id}/attachments/${att.id}/file`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e: any) {
      setHeaderError(errDetail(e, "Faylni ochib bo'lmadi"));
    }
  };

  const filteredThreads = threads.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.customer_name || '').toLowerCase().includes(q) || (t.customer_username || '').toLowerCase().includes(q);
  });

  return (
    <div className="h-[calc(100vh-64px)] flex">
      {/* Thread ro'yxati */}
      <div className={`w-full md:w-80 lg:w-96 border-r bg-white flex-col flex-shrink-0 ${mobileView === 'detail' ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-3 border-b space-y-2">
          <h1 className="text-lg font-bold text-gray-800">So'rovlar</h1>
          <input type="text" placeholder="🔍 Mijozni qidirish..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Barcha holatlar</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="flex flex-wrap gap-1.5">
            {ASSIGNED_FILTERS.map(af => (
              <button key={af.value || 'all'} onClick={() => setAssignedFilter(af.value)}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${assignedFilter === af.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {af.label}
              </button>
            ))}
            <button onClick={() => setUnreadOnly(v => !v)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${unreadOnly ? 'bg-red-500 text-white border-red-500' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {unreadOnly ? '✓ ' : ''}Faqat o'qilmagan
            </button>
          </div>
        </div>

        {listError && <p className="text-xs text-red-600 px-3 py-2 border-b bg-red-50">{listError}</p>}

        <div className="flex-1 overflow-y-auto">
          {loadingList ? (
            <p className="text-center text-gray-400 text-sm py-8">Yuklanmoqda...</p>
          ) : filteredThreads.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8">So'rovlar yo'q</p>
          ) : (
            filteredThreads.map(t => {
              const st = STATUS_CONFIG[t.status] || { label: t.status, color: 'bg-gray-100 text-gray-600' };
              return (
                <div key={t.id} onClick={() => openThread(t.id)}
                  className={`px-4 py-3 border-b cursor-pointer transition hover:bg-gray-50 ${selectedId === t.id ? 'bg-blue-50' : ''}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {t.unread_count > 0 && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                        <p className={`text-sm truncate ${t.unread_count > 0 ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>
                          {t.customer_name || t.customer_username || `Chat ${t.id}`}
                        </p>
                      </div>
                      {t.last_message_snippet && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{t.last_message_snippet}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.color}`}>{st.label}</span>
                        {t.assigned_user_name
                          ? <span className="text-[10px] text-gray-500 truncate">👤 {t.assigned_user_name}</span>
                          : <span className="text-[10px] text-orange-500">biriktirilmagan</span>}
                        {t.customer_id && <span className="text-[10px] text-green-600" title="Mijoz bazada">✓ bazada</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[10px] text-gray-400">{timeShort(t.last_message_at)}</span>
                      {t.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                          {t.unread_count > 99 ? '99+' : t.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Suhbat paneli */}
      <div className={`flex-1 flex-col bg-gray-50 min-w-0 ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`}>
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <p>So'rovni tanlang</p>
          </div>
        ) : loadingDetail ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <p>Yuklanmoqda...</p>
          </div>
        ) : detailError || !detail ? (
          <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
            <p>{detailError || 'Xato'}</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-white border-b px-4 py-3 flex items-center gap-3 flex-wrap">
              <button className="md:hidden text-gray-500 text-lg" onClick={() => setMobileView('list')}>←</button>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 truncate">{detail.customer_name || detail.customer_username || `Chat ${detail.id}`}</p>
                <p className="text-xs text-gray-400">
                  {detail.customer_username ? `@${detail.customer_username}` : ''}
                  {detail.customer_phone ? ` · ${detail.customer_phone}` : ''} · Telegram
                </p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  {detail.customer ? (
                    <Link to="/clients" className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200"
                      title="Mijozlar bo'limida ochish">
                      ✓ {customerDisplayName(detail.customer)} · bazada
                    </Link>
                  ) : canCreateCustomer && !showCustomerForm ? (
                    <button onClick={() => setShowCustomerForm(true)}
                      className="text-xs px-2 py-0.5 rounded border border-green-600 text-green-700 hover:bg-green-50">
                      + Mijozni bazaga qo'shish
                    </button>
                  ) : !canCreateCustomer ? (
                    <span className="text-xs text-gray-400">Mijoz bazada emas</span>
                  ) : null}
                  {customerJustAdded && <span className="text-xs text-green-600 font-medium">✓ Bazaga qo'shildi</span>}
                </div>
              </div>
              <select value={detail.status} onChange={e => handleStatusChange(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={detail.assigned_user_id || ''} onChange={e => handleAssignChange(e.target.value)}
                title="Mas'ul xodim"
                className={`border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${detail.assigned_user_id ? '' : 'text-orange-600'}`}>
                <option value="" disabled={!!detail.assigned_user_id}>👤 Biriktirilmagan</option>
                {/* Joriy egasi ro'yxatda bo'lmasa ham (masalan roli o'zgargan) ko'rinib tursin */}
                {detail.assignee && !assignableUsers.some(u => u.id === detail.assignee!.id) && (
                  <option value={detail.assignee.id}>👤 {detail.assignee.full_name}</option>
                )}
                {assignableUsers.map(u => <option key={u.id} value={u.id}>👤 {u.full_name}</option>)}
              </select>
            </div>
            {(headerError || assignableError) && (
              <div className="bg-red-50 border-b px-4 py-1.5 text-xs text-red-600">
                {headerError || assignableError}
              </div>
            )}
            {showCustomerForm && !detail.customer && (
              <InquiryCustomerForm threadId={detail.id} onCreated={handleCustomerCreated}
                onCancel={() => setShowCustomerForm(false)} />
            )}

            {/* Xabarlar */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {detail.messages.map(m => (
                <InquiryMessageBubble key={m.id} threadId={detail.id} message={m}
                  canManage={canManageMessage(m)}
                  telegramCanDelete={!!detail.telegram_can_delete}
                  onUpdated={handleMessageUpdated}
                  onDownload={downloadAttachment} />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Javob paneli */}
            <div className={`border-t p-3 ${replyMode === 'note' ? 'bg-yellow-50' : 'bg-white'}`}>
              <div className="flex gap-1 mb-2 text-xs">
                <button onClick={() => { setReplyMode('reply'); setReplyError(''); }}
                  className={`px-3 py-1 rounded-lg border ${replyMode === 'reply' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  ✉️ Mijozga javob
                </button>
                <button onClick={() => { setReplyMode('note'); setReplyError(''); }}
                  className={`px-3 py-1 rounded-lg border ${replyMode === 'note' ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  🔒 Ichki eslatma
                </button>
              </div>
              {replyMode === 'note' && (
                <p className="text-xs text-yellow-800 mb-2">⚠️ Ichki eslatma mijozga yuborilmaydi — faqat xodimlar ko'radi.</p>
              )}
              {replyError && <p className="text-xs text-red-600 mb-2">{replyError}</p>}
              {replyMode === 'reply' && replyFile && (
                <div className="flex items-center gap-2 mb-2 text-xs bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5">
                  <span>📎</span>
                  <span className="truncate font-medium text-gray-800">{replyFile.name}</span>
                  <span className="text-gray-500 shrink-0">({formatFileSize(replyFile.size)})</span>
                  <span className="text-gray-500 hidden sm:inline shrink-0">· matn maydoni = fayl izohi (ixtiyoriy)</span>
                  <button onClick={clearReplyFile} disabled={sending} title="Faylni olib tashlash"
                    className="ml-auto text-gray-500 hover:text-red-600 px-1">✕</button>
                </div>
              )}
              <div className="flex items-end gap-2">
                {replyMode === 'reply' && (
                  <>
                    <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} onChange={handleFilePicked} className="hidden" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={sending} title={`Fayl biriktirish (${MAX_ATTACHMENT_MB} MB gacha)`}
                      className="border rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                      📎
                    </button>
                  </>
                )}
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={handleKeyDown}
                  rows={2} placeholder={replyMode === 'note' ? 'Ichki eslatma yozing...' : replyFile ? 'Fayl izohi (ixtiyoriy)...' : 'Javob yozing (Telegramga yuboriladi)...'}
                  className={`flex-1 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 ${replyMode === 'note' ? 'border-yellow-400 focus:ring-yellow-500 bg-white' : 'focus:ring-blue-500'}`} />
                <button onClick={handleSend} disabled={sending || (!replyText.trim() && !(replyMode === 'reply' && replyFile))}
                  className={`text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${replyMode === 'note' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  {sending ? '...' : replyMode === 'note' ? 'Eslatma qo\'shish' : 'Yuborish'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Inquiries;
